import React, { useState, useEffect, useCallback, createContext, useContext } from "react";
import * as c from "./carousel.module.scss";
import useEmblaCarousel from "embla-carousel-react";

import Autoplay from "embla-carousel-autoplay";
import classNames from "classnames";

const CarouselContext = createContext();
export default function Carousel({ className, children, delay, config, arrow, ...props }) {
  const [emblaRef, emblaApi] = useEmblaCarousel(config, [Autoplay({ delay: delay || 4000 })]);

  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollTo = useCallback((index) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);
  const onSelect = useCallback(() => setSelectedIndex(emblaApi.selectedScrollSnap()), [emblaApi]);

  const onReInit = useCallback(() => {
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
  }, [emblaApi, onSelect]);

  const onSlideClick = useCallback(
    (event) => {
      if (emblaApi && emblaApi.clickAllowed()) return;
      event.preventDefault();
    },
    [emblaApi]
  );

  useEffect(() => {
    if (typeof window !== `undefined` && emblaApi) {
      emblaApi.on("reInit", onReInit);
      emblaApi.on("select", onSelect);
      emblaApi.reInit();

      return () => {
        emblaApi.off("reInit", onReInit);
        emblaApi.off("select", onSelect);
      };
    }
  }, [emblaApi, onReInit, onSelect]);

  return (
    <CarouselContext.Provider value={{ scrollSnaps, selectedIndex, scrollTo, onSlideClick }}>
      <div
        className={classNames("carousel-notouch", className, c.carousel)}
        ref={emblaRef}
        style={{ "--transition-duration": delay ? `${delay}ms` : "0.2s" }}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

Carousel.Slider = ({ className, children, transition }) => (
  <div data-transition={transition} className={classNames(className, c.slider)}>
    {children}
  </div>
);
Carousel.Slide = ({ className, children, slideNumber }) => {
  const { selectedIndex, onSlideClick } = useContext(CarouselContext);

  return (
    <div
      className={classNames(className, c.slide)}
      data-active={slideNumber === selectedIndex ? "true" : "false"}
      onClick={() => {
        onSlideClick();
      }}
    >
      {children}
    </div>
  );
};

Carousel.Controls = ({ className, controlText, isHome, style = "line", theme = "light" }) => {
  const { scrollSnaps, selectedIndex, scrollTo, onSlideClick } = useContext(CarouselContext);
  const [stopped, setStopped] = useState(false);
  return (
    <div className={classNames(className, c.controls, controlText && isHome && c.hasText)}>
      {scrollSnaps.map((_, index) => (
        <button
          key={index}
          type="button"
          aria-label={`Slide ${index}`}
          style={{ ...(stopped && { "--transition-duration": "0s" }) }}
          onClick={() => {
            onSlideClick(), scrollTo(index), setStopped(true);
          }}
          className={classNames(c.controlStyle, c[style], c[theme], {
            [c.selected]: index === selectedIndex,
            selected: index === selectedIndex,
          })}
        >
          {controlText && isHome ? <span>{controlText[index]}</span> : <span></span>}
        </button>
      ))}
    </div>
  );
};

Carousel.Arrow = ({ className, arrowText, mobile }) => {
  const { selectedIndex } = useContext(CarouselContext);
  return (
    <div className={classNames(className, "carousel-arrow")}>
      {mobile ? (
        <svg width="30" height="32" viewBox="0 0 30 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M5.16282 30.5874C9.65755 8.8535 21.1241 2.22581 29 0.999915"
            stroke="#B7BCE6"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M11.7852 22.4257C11.7852 22.4257 7.04234 27.4141 5.11326 31C5.11326 31 2.91112 22.1555 0.999795 19.8585"
            stroke="#B7BCE6"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="76" height="83" viewBox="0 0 76 83" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 80C23.8773 21.9694 54.1775 4.27316 74.9893 0.999962"
            stroke="#B7BCE6"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M29.4998 58.2077C29.4998 58.2077 16.967 71.527 11.8694 81.1016C11.8694 81.1016 6.05031 57.4862 0.999665 51.3533"
            stroke="#B7BCE6"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {arrowText &&
        arrowText.map((text, index) => (
          <p key={text} data-active={index === selectedIndex ? "true" : "false"}>
            {arrowText[index]}
          </p>
        ))}
    </div>
  );
};
